/**
 * Which property the app treats as active, and the sync signal that makes
 * that answer trustworthy. Consumed by `AppProvider` (see use-powersync.tsx);
 * split out so the resolution can be exercised directly, without a provider.
 */

import type { SyncStatus } from "@powersync/web";
import type { Property } from "@/lib/collections/properties";

/**
 * The auto-subscribed stream carrying the property catalog (see
 * `powersync/sync-config.yaml`). Its first sync is the moment
 * `allProperties` becomes the complete set of properties the user can
 * reach, rather than however much has landed so far.
 */
const PROPERTY_CATALOG_STREAM = "bootstrap";

/**
 * Whether the property catalog has synced at least once.
 *
 * Distinct from `SyncStatus.hasSynced`, which latches on the first full
 * sync of any priority and is already true — restored from the local DB —
 * before the catalog stream delivers its first rows.
 *
 * PowerSync persists per-stream sync state in the local database and
 * restores it at init, so a previously-synced device reports true with no
 * network involved.
 */
export function readPropertyCatalogSynced(status: SyncStatus): boolean {
  // Anything short of the catalog stream reporting its own first sync
  // counts as "not yet" — including a status that lists no catalog stream
  // at all. Treating an absent entry as synced would trust a stream list
  // that may still be filling in, reopening the very race this closes.
  // Failing closed costs nothing: no catalog stream means no catalog rows,
  // so the resolution below has nothing to fall back to either way.
  return (
    status.syncStreams?.some(
      (s) =>
        s.subscription.name === PROPERTY_CATALOG_STREAM &&
        s.subscription.hasSynced
    ) ?? false
  );
}

/**
 * Resolve the active property from the session's `activeOrganizationId`,
 * falling back to the first accessible property.
 *
 * `allProperties` fills in incrementally as PowerSync syncs, in no
 * particular order, so "the active property's row hasn't arrived" must not
 * be answered with "here is a different property": a host with two
 * properties cold-booting a deep link would act on the wrong tenant's data
 * for as long as the gap lasts.
 *
 * The fallback therefore applies in exactly two cases — the session names
 * no property at all (a fresh install, before `activeOrganizationId` is
 * populated), or the catalog has fully synced and the property it names
 * still isn't there. The second case is a pointer that outlived its
 * membership: better-auth seeds `activeOrganizationId` at session creation
 * and never clears it when a membership is removed, so treating an
 * unmatched id as permanently unresolved would leave a host who still has
 * other properties with no active property until they re-login.
 */
export function resolvePrimaryProperty({
  activeOrgId,
  allProperties,
  catalogSynced,
}: {
  activeOrgId: string | null;
  allProperties: Property[];
  catalogSynced: boolean;
}): Property | null {
  if (activeOrgId) {
    const match = allProperties.find((p) => p.id === activeOrgId);
    if (match) {
      return match;
    }
    if (!catalogSynced) {
      return null;
    }
  }
  return allProperties[0] ?? null;
}
